import { IS_BROWSER } from '../constants';

/** Hosts that belong to this site, so absolute CMS links to them stay internal. */
const SITE_HOSTNAMES = ['aatosklinikka.fi', 'www.aatosklinikka.fi'];

const HAS_SCHEME = /^[a-z][a-z0-9+.-]*:/i;

const toRootRelative = (path: string) => `/${path.replace(/^\/+/, '')}`;

/** Splits "host:port/rest?query#hash" into its hostname and everything after. */
const splitHost = (value: string) => {
  const [authority, ...rest] = value.split(/(?=[/?#])/);
  return {
    hostname: authority.split(':')[0].toLowerCase(),
    path: rest.join(''),
  };
};

/**
 * Turns a URL entered in Strapi into one that resolves the way the editor
 * meant it to. Editors regularly omit the leading slash ("kampanja/x"), which
 * the browser resolves against the *current* directory — on a campaign page
 * that becomes /kampanja/kampanja/x. Internal links are therefore always
 * anchored to the site root, and links written as a bare aatosklinikka.fi
 * address are rewritten to that same root-relative form.
 */
export const resolveContentUrl = (url?: string | null): string => {
  const value = (url ?? '').trim();
  if (!value) return '';

  // Anchors and protocol-relative URLs are already unambiguous.
  if (value.startsWith('#') || value.startsWith('//')) return value;

  if (HAS_SCHEME.test(value)) {
    const httpUrl = /^https?:\/\/(.*)$/i.exec(value);
    if (!httpUrl) return value; // mailto:, tel:, ...
    const { hostname, path } = splitHost(httpUrl[1]);
    return SITE_HOSTNAMES.includes(hostname) ? toRootRelative(path) : value;
  }

  // A scheme-less host, e.g. "www.aatosklinikka.fi/kampanja/x". Only the site
  // hosts and "www." prefixes count, so a filename like "esite.pdf" is still
  // read as a path.
  const { hostname, path } = splitHost(value);
  if (SITE_HOSTNAMES.includes(hostname)) return toRootRelative(path);
  if (hostname.startsWith('www.')) return `https://${value}`;

  return toRootRelative(value);
};

/**
 * True when `resolveContentUrl` produced a link to another site, i.e. one
 * worth opening in a new tab. `mailto:`/`tel:` are excluded: handing those to
 * a new tab just leaves an empty one behind.
 */
export const isExternalUrl = (url?: string | null): boolean =>
  /^(https?:)?\/\//i.test((url ?? '').trim());

export const getUrlParam = (name: string) => {
  if (!IS_BROWSER) {
    return null;
  }

  try {
    const params = new URLSearchParams(location.search);
    return params.get(name);
  } catch (error) {
    return null;
  }
};

export const setUrlParam = (name: string, value: number | string) => {
  if (!IS_BROWSER) {
    return;
  }

  try {
    const params = new URLSearchParams(location.search);

    if (value) {
      params.set(name, `${value}`);
    } else {
      params.delete(name);
    }

    history.replaceState({}, '', `${location.pathname}?${params}`);
  } catch (error) {}
};
